import { useState, useMemo } from 'react';
import { useEntityInstanceWorkflowAction } from '../api/endpoints/useEntityInstanceWorkflowAction';
import { useSuccessMessage } from '../utils/successMessages';
import type { EntityDetailsAction } from '../types/actions';
import { useToast } from '@locus-taxy/encore-ui-toolkit';
import { buildClientPrefixedPath } from '../utils/url';
import { useNavigate } from 'react-router-dom';

interface UseWorkflowActionsProps {
  clientId: string;
  entityName: string;
  entityId?: string;
  refreshData?: () => void;
  onClearSelection?: () => void;
  onCloseModal?: () => void;
}

type ModalVariantType =
  | {
      variant: 'drawer';
      drawerWidth: number;
    }
  | {
      variant: 'modal';
    };

export function useWorkflowActions({
  clientId,
  entityName,
  entityId,
  refreshData,
  onClearSelection,
  onCloseModal,
}: UseWorkflowActionsProps) {
  const { showSuccess } = useToast();
  const [currentAction, setCurrentAction] =
    useState<EntityDetailsAction | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const workflowAction = useEntityInstanceWorkflowAction(
    clientId,
    entityName,
    entityId ?? ''
  );

  const { generateSuccessMessage } = useSuccessMessage();

  const handleActionClick = (action: EntityDetailsAction) => {
    setCurrentAction(action);
  };

  const handleFormSubmit = (formData: Record<string, unknown>) => {
    if (!currentAction) {
      return;
    }

    setIsLoading(true);

    if (currentAction.completionAction?.type === 'workflow') {
      workflowAction.mutate(
        {
          actionId: currentAction.id,
          actionReference: currentAction.reference,
          variables: formData,
        },
        {
          onSuccess: () => {
            // Show success toast
            const message = generateSuccessMessage('action', '');
            showSuccess(message);

            // Close modal and refresh
            setCurrentAction(null);
            onCloseModal?.();
            if (currentAction?.id?.includes('deleteEntityAction')) {
              //navigate to entityName page
              return navigate(
                buildClientPrefixedPath(`/entities/${entityName}`, clientId)
              );
            }
            refreshData?.();
            onClearSelection?.();
            setIsLoading(false);
          },
          onError: () => {
            setIsLoading(false);
          },
        }
      );
    } else {
      // For non-workflow actions, just show success and close
      const message = generateSuccessMessage('action', '');
      showSuccess(message);
      setCurrentAction(null);
      onCloseModal?.();
      refreshData?.();
      onClearSelection?.();
      setIsLoading(false);
    }
  };

  const handleCloseModal = () => {
    setCurrentAction(null);
    workflowAction.reset();
  };

  const modalVariant: ModalVariantType = useMemo(() => {
    if (currentAction?.id?.includes('updateEntityAction')) {
      return {
        variant: 'drawer',
        drawerWidth: 900,
      };
    }
    return {
      variant: 'modal',
    };
  }, [currentAction]);

  return {
    // State
    currentWorkflowAction: currentAction,
    isWorkflowLoading: isLoading || workflowAction.isPending,
    modalVariant,

    // Actions
    handleWorkflowActionClick: handleActionClick,
    handleWorkflowFormSubmit: handleFormSubmit,
    handleWorkflowCloseModal: handleCloseModal,
  };
}
